import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/theme.css';
import App from './App';
import host from './host';
import { initNativeChrome } from './platform/nativeChrome';
import { ensureBackendBinding } from './platform/backendBinding';

initNativeChrome();

// F12a-N task-n8 Fix B — the backend-binding / foreign-session reset check
// must resolve BEFORE anything in the React tree mounts, full stop. It used
// to live entirely inside PosAppShell's own effect (still there as a
// secondary, now-usually-redundant defense layer — see PosAppShell.js), but
// PosAppShell is a DESCENDANT of AuthProvider (App.js nests
// <AuthProvider><Router><PosAppShell/></Router></AuthProvider>).
// AuthContext's own boot effect starts restoring a persisted session (and
// can begin replaying a foreign token — setting axios headers, firing
// requests) the moment AuthProvider mounts; a check that only runs inside
// a CHILD component's effect is racing an independent async boot sequence
// in an ANCESTOR, not something a single deterministic effect-firing order
// can guarantee away. Running the check here, before `createRoot(...).
// render()` is ever called, removes the race by construction — no
// component of any kind exists yet, so nothing can read or replay a
// foreign session while this is in flight. Capacitor-gated (no-op on
// web/Electron, same as the module's own internal check) so render
// proceeds immediately there.
//
// Exported (rather than an anonymous bottom-of-file IIFE) so index.test.js
// can drive it directly with a test DOM node and mocked collaborators,
// instead of needing `jest.resetModules()` to re-run module-top-level code
// — that approach creates a second, disconnected 'react'/'react-dom'
// module instance on re-require, which silently breaks React's act()
// tracking. The real, one-time boot call is guarded below.
export async function boot(rootEl) {
    if (host.type === 'capacitor') {
        try {
            await ensureBackendBinding();
        } catch (err) {
            // Never let a binding-check failure block boot.
        }
    }
    ReactDOM.createRoot(rootEl).render(
        <React.StrictMode>
            <App />
        </React.StrictMode>
    );
}

// Real boot call — skipped under Jest (NODE_ENV === 'test', set
// automatically by CRA's test runner) so index.test.js can import this
// module and call `boot()` itself, explicitly, once a test DOM root and
// mocks are in place.
if (process.env.NODE_ENV !== 'test') {
    boot(document.getElementById('root'));
}

// Register service worker only for web (not Electron)
if ('serviceWorker' in navigator && host.wantsServiceWorker) {
    window.addEventListener('load', () => {
        navigator.serviceWorker
            .register('/sw.js')
            .then(registration => {
                console.log('Service Worker registered successfully:', registration.scope);

                // Listen for updates
                registration.addEventListener('updatefound', () => {
                    const newWorker = registration.installing;
                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            // New version available, prompt user to reload
                            if (window.confirm('New version available! Click OK to update.')) {
                                window.location.reload();
                            }
                        }
                    });
                });
            })
            .catch(error => {
                console.error('Service Worker registration failed:', error);
            });
    });
} else if (!host.wantsServiceWorker) {
    console.log(` ${host.type} detected - Service Worker disabled`);
}